import React, { Fragment, isValidElement } from "react";
import { isFunction, map, size } from "lodash";
import styled from "styled-components";
import { Spacing } from "@glossy/spacings";
import { content } from "@glossy/theme";
import { tr } from "@linkfluence/i18n";
import { Locale } from "@linkfluence/models/locale";
import Button, { Variant } from "@ui-kit/button";
import Tooltip from "@ui-kit/tooltip";

export type CustomButton =
  | React.ReactElement
  | {
      label: string;
      variant?: Variant;
      action: () => void;
    };

interface IProps {
  // Action when click on Ok button
  onValidate?: () => void;
  // Label for close footer
  validateLabel?: string;
  // Action when click on close
  onCancel?: () => void;
  // Label for close footer
  cancelLabel?: string;
  // Custom footer and actions (override validate and close)
  customButtons?: CustomButton[];
  // If false, validate button is disabled
  isValid?: boolean;
  locale: Locale;
  invalidText?: string;
}

const Footer = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: ${Spacing.Large};
  gap: ${Spacing.ExtraSmall};
  border-top: 1px solid ${(props) => content(props, "tertiary")};
`;

const UiKitModalFooter = ({
  onValidate,
  validateLabel,
  onCancel,
  cancelLabel,
  customButtons,
  isValid = true,
  locale,
  invalidText = "",
}: IProps) => (
  <>
    {/* Display footer if there is no custom buttons or if there is at least one custom button  */}
    {((!customButtons && (isFunction(onCancel) || isFunction(onValidate))) ||
      size(customButtons) > 0) && (
      <Footer>
        {customButtons ? (
          map(customButtons, (button, id) =>
            isValidElement(button) ? (
              <Fragment key={id}>{button}</Fragment>
            ) : (
              <Button
                key={id}
                text={button.label}
                variant={button.variant}
                onClick={button.action}
              />
            )
          )
        ) : (
          <>
            {onCancel && (
              <Button
                data-testid="modal-button-cancel"
                variant="tertiary"
                size="m"
                text={cancelLabel || tr(locale, "MODAL::CANCEL")}
                onClick={onCancel}
              />
            )}
            {onValidate && (
              <Tooltip
                tooltip={<>{invalidText}</>}
                disabled={!invalidText || isValid}
              >
                <Button
                  data-testid="modal-button-validate"
                  variant="primary"
                  size="m"
                  text={validateLabel || tr(locale, "MODAL::OK")}
                  onClick={onValidate}
                  isDisabled={!isValid}
                />
              </Tooltip>
            )}
          </>
        )}
      </Footer>
    )}
  </>
);

export default UiKitModalFooter;
